/* ======================================================
   PRODUCT INCLUSIONS — OPTIONAL + MANDATORY (STABLE)
   + TIME-BASED OPTIONS (PDP batch add)

   TRUE BATCH MODE (DROP-IN):
   - PDP:
       * Default: add main + checked optionals in ONE request
       * If Time-Based Options exists: add ONLY the selected option's products in ONE request
   - CART: update one group (main + its inclusions) in ONE request (one refresh)
   - No MutationObserver / no fetch intercept / no loops

   Group link:
   - Addons have properties._mfw_group = "<GROUP_ID>"
   - Addons have properties._mfw_user_selected = "true"

   NOTES FOR TIME-BASED OPTIONS:
   - We add ONLY the selected option’s products (no main product)
   - We group them by setting _mfw_group on ALL of them
   - Cart behavior: because they have _mfw_group, your cart sync treats edits as standalone (no auto-delta)

   FINAL FIX (AUTHORITATIVE):
   - When MFW handles Add-to-Cart, native Shopify form submission is BLOCKED
====================================================== */

(function () {
  if (window.__mfw_product_inclusions_booted) return;
  window.__mfw_product_inclusions_booted = true;

  /* =========================
     GLOBAL FLAGS
  ========================= */
  window.__mfw_handling_atc = false;

  /* =========================
     DEBUG UTILITIES
  ========================= */
  /* This file logs its whole add-to-cart decision — which system owns the
     button, whether the main product is being excluded, and exactly what gets
     POSTed — but it was all dead code behind a constant that had to be edited
     and deployed to see anything. Now it can be switched on from the browser:

       ?mfwdebug=1                      one page load
       localStorage.mfwDebug = '1'      until cleared

     Off by default, so nothing is logged for customers. */
  const DEBUG_MFW = (function () {
    try {
      if (/[?&]mfwdebug=1/.test(window.location.search)) { return true; }
      return window.localStorage && window.localStorage.getItem('mfwDebug') === '1';
    } catch (e) { return false; }
  }());

  function dlog(...args) {
    if (!DEBUG_MFW) return;
    console.log('%c[MFW]', 'color:#6b46c1;font-weight:bold;', ...args);
  }

  /* =========================
     DOM HELPERS
  ========================= */
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => Array.from(c.querySelectorAll(s));

  /* =========================
     CRITICAL FIX:
     BLOCK NATIVE SHOPIFY FORM SUBMIT
     ONLY WHILE MFW IS HANDLING ATC
  ========================= */
  document.addEventListener(
    'submit',
    function (e) {
      if (!window.__mfw_handling_atc) return;

      const form = e.target;
      if (!form || !form.action) return;

      if (form.action.indexOf('/cart/add') !== -1) {
        e.preventDefault();
        e.stopImmediatePropagation();
        dlog('BLOCK → native Shopify form submit (duplicate prevention)');
        return false;
      }
    },
    true
  );

  // =========================
  // UFE DETECTION (gate) — stricter
  // =========================
  function hasUFEOnPage() {
    // Prefer strong signals first (data attributes used by UFE widgets)
    const strong = document.querySelector(
      [
        '[data-ufe]',
        '[data-ufe-widget]',
        '[data-ufe-offer]',
        '[data-app="ufe"]'
      ].join(',')
    );

    if (strong) return true;

    // Weak signals (class/id substrings) create false positives.
    // Only treat as UFE if we ALSO see likely UFE context on the page.
    const weak = document.querySelector(
      [
        '[id*="ufe"]',
        '[class*="ufe"]',
        '[class*="UFE"]'
      ].join(',')
    );

    if (!weak) return false;

    // Additional confirmation: UFE commonly injects offer-related markup or scripts.
    // This is a best-effort heuristic; if you want ZERO false positives, remove this block
    // and rely only on the strong selectors above.
    const maybeUfeScript = Array.from(document.scripts || []).some((s) =>
      (s.src || '').toLowerCase().includes('ufe')
    );

    return !!maybeUfeScript;
  }



  /* =========================
     SHARED HELPERS
  ========================= */
  const TREAT_UNTRACKED_AS_DISABLED = true; // set false if you want untracked items purchasable

  function getAvailability(tracked, inv, back, qty, stocksFromSuppliers) {
    // If inventory is not tracked:
    // - allow ONLY when stocksFromSuppliers is true
    // - otherwise keep existing behavior (disabled + "untracked")
    if (!tracked) {
      if (stocksFromSuppliers) {
        return { ok: true, reason: '' };
      }
      return {
        ok: !TREAT_UNTRACKED_AS_DISABLED,
        reason: TREAT_UNTRACKED_AS_DISABLED ? 'untracked' : ''
      };
    }

    const ok = inv >= qty || back;
    return { ok, reason: ok ? '' : 'oos' };
  }


  function applyOptionalState(block, ok, reason) {
    block.classList.toggle('is-disabled', !ok);
    block.dataset.disabledReason = !ok ? (reason || 'oos') : '';

    const cb = qs('[data-optional-checkbox]', block);
    if (cb) {
      cb.disabled = !ok;
      if (!ok) cb.checked = false;
    }
  }

  function getQty() {
    return parseInt(qs('input[name="quantity"]')?.value, 10) || 1;
  }

  function findAddBtn(form) {
    if (!form) form = findForm();
    if (!form) return null;

    // Prefer the regular "Add to cart" button inside the product form
    // Avoid Shop Pay / dynamic checkout buttons.
    return (
      qs('button[name="add"]', form) ||
      qs('button[type="submit"][name="add"]', form) ||
      qs('button[type="submit"]:not(.shopify-payment-button__button):not([name="checkout"])', form) ||
      qs('input[type="submit"][name="add"]', form) ||
      qs('button[type="submit"]', form)
    );
  }


  function findForm() {
    return qs('form[action*="/cart/add"]');
  }

  function getMainVariantId(form) {
    return (
      qs('input[name="id"]', form)?.value ||
      qs('select[name="id"]', form)?.value ||
      null
    );
  }

  async function fetchCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!r.ok) throw new Error('cart.js failed');
    return r.json();
  }

  async function postUpdate(updates) {
    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates })
    });
    if (!r.ok) throw new Error('cart/update failed');
    return r.json().catch(() => null);
  }

  function clampQty(n) {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.max(0, Math.floor(x));
  }

  async function postAddItems(items) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items })
    });

    const text = await r.text(); // read body once

    if (!r.ok) {
      dlog('CART/ADD FAILED', { status: r.status, body: text });
      throw new Error(`cart/add failed (${r.status})`);
    }

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  function hasInclusionsOnPage() {
    return !!(
      document.querySelector('.mandatory-inclusions') ||
      document.querySelector('[data-optional-group]') ||
      document.querySelector('[data-tbo-group]')
    );
  }

  function isExcludeMainProductEnabled() {
    const el = document.querySelector('[data-mfw-exclude-main-product]');
    if (!el) return false;

    const raw = (el.getAttribute('data-mfw-exclude-main-product') || '').trim();
    if (!raw) return false;

    // Handles: true / false / "true" / "false"
    try {
      const v = JSON.parse(raw);
      return v === true;
    } catch (e) {
      return raw.toLowerCase() === 'true';
    }
  }

  // ───── exclude-main-from-cart helpers ───────────────────────────────────
  // Mandatory uses an "always exclude" semantic — flag set means main product
  // is never added regardless of selections.
  // Optional uses a "conditional exclude" semantic — flag set on a group means
  // main product is excluded ONLY if at least one optional in that group is
  // selected at ATC time. Different semantics so we evaluate them separately.

  // True if mandatory wrap has the exclude flag set (always-exclude).
  function isMandatoryExcludeMain() {
    const el = document.querySelector('.mandatory-inclusions[data-mfw-exclude-main-product]');
    if (!el) return false;
    const raw = (el.getAttribute('data-mfw-exclude-main-product') || '').trim();
    try { return JSON.parse(raw) === true; }
    catch (e) { return raw.toLowerCase() === 'true'; }
  }

  // True if ANY optional group has its exclude-main flag set (regardless of
  // current selection state). Used at init time to decide if MFW must take
  // over ATC handling — we cannot defer to UFE if exclude is even possible.
  function hasOptionalExcludeMainFlag() {
    return !!document.querySelector('[data-optional-group][data-optional-exclude-main="true"]');
  }

  // True if ANY optional group with the exclude-main flag has at least one
  // checked addon RIGHT NOW. Used at recompute / click time to decide whether
  // to actually exclude main from cart this turn.
  function isOptionalExcludingMainNow(scope) {
    const root = scope || document;
    const groups = root.querySelectorAll('[data-optional-group][data-optional-exclude-main="true"]');
    for (let i = 0; i < groups.length; i++) {
      if (groups[i].querySelector('input[data-optional-checkbox]:checked')) {
        return true;
      }
    }
    return false;
  }

  // Combined dynamic check: true if main should be excluded from cart RIGHT NOW.
  // (Mandatory always-exclude OR optional conditional-exclude triggered.)
  function computeExcludeMainNow(scope) {
    if (isMandatoryExcludeMain()) return true;
    if (isOptionalExcludingMainNow(scope)) return true;
    return false;
  }
  // ────────────────────────────────────────────────────────────────────────



  function safeId(raw) {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function toCents(raw) {
    const n = Number(raw);
    return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
  }

  function buildInclusionProperties({
    groupId,
    inclusionType,
    mainProductId,
    mainVariantId,
    inclusionProductId,
    baseVariantId,
    discountVariantId,
    originalPriceCents,
    discountedPriceCents,
    section = ''
  }) {
    const original = toCents(originalPriceCents);
    const discounted = toCents(discountedPriceCents);
    const discountAmount = Math.max(0, original - discounted);

    return {
      _mfw_group: String(groupId || ''),
      _mfw_user_selected: 'true',
      _mfw_inclusion_type: String(inclusionType || ''),
      _mfw_main_product_id: String(mainProductId || ''),
      _mfw_main_variant_id: String(mainVariantId || ''),
      _mfw_parent_product_id: String(mainProductId || ''),
      _mfw_inclusion_product_id: String(inclusionProductId || ''),
      _mfw_inclusion_base_variant_id: String(baseVariantId || ''),
      _mfw_inclusion_discount_variant_id: String(discountVariantId || ''),
      _mfw_original_price_cents: String(original),
      _mfw_discounted_price_cents: String(discounted),
      _mfw_discount_amount_cents: String(discountAmount),
      _mfw_section: String(section || '')
    };
  }



  /* =========================
     OPTIONAL GROUPS (PDP)
  ========================= */

  function initOptionalGroups() {
    const groups = qsa('[data-optional-group]');
    if (!groups.length) return;

    groups.forEach((group) => {
      const addons = qsa('[data-optional-addon]', group);

      const validateGroup = () => {
        const qty = getQty();

        addons.forEach((block) => {
          const cb = qs('[data-optional-checkbox]', block);
          if (!cb) return;

          const tracked = String(block.dataset.tracked || '') === 'true';
          const inv = parseInt(block.dataset.inventory || '0', 10) || 0;
          const back = block.dataset.allowsBackorder === 'true';

          const stocksFromSuppliers = String(block.dataset.stocksFromSuppliers || '') === 'true';
          const { ok, reason } = getAvailability(tracked, inv, back, qty, stocksFromSuppliers);


          applyOptionalState(block, ok, reason);
        });

      };

      // Initial validation
      validateGroup();

      // Checkbox change
      addons.forEach((block) => {
        const cb = qs('[data-optional-checkbox]', block);
        if (!cb) return;
        cb.addEventListener('change', validateGroup);
      });

      // Quantity change
      document.addEventListener('change', (e) => {
        if (e.target?.name === 'quantity') validateGroup();
      });
    });
  }

  function initOptionalCardDisabling() {
    const qty = getQty();

    qsa('[data-optional-addon]').forEach((block) => {
      const tracked = String(block.dataset.tracked || '') === 'true';
      const inv = parseInt(block.dataset.inventory || '0', 10) || 0;
      const back = block.dataset.allowsBackorder === 'true';

      const stocksFromSuppliers = String(block.dataset.stocksFromSuppliers || '') === 'true';
      const { ok, reason } = getAvailability(tracked, inv, back, qty, stocksFromSuppliers);


      applyOptionalState(block, ok, reason);
    });

  }



  /* =========================
     OPTIONAL GROUPS: REQUIRED (PDP)

     The metaobject's `required` boolean used to change nothing but the
     "(Required)" label — an empty required group still added to cart, so an RO
     system could ship with no faucet chosen.

     Both guards below bind on document in the CAPTURE phase. Every other
     add-to-cart handler on the page (the theme's, UFE's, and this file's own
     takeover in initAddToCart) binds in the bubble phase, so capturing here
     blocks the click whichever of them owns the button — including the paths
     where initAddToCart returns early and never binds at all.
  ========================= */

  function requiredOptionalGroups() {
    return qsa('[data-optional-group][data-required="true"]');
  }

  // A group nobody CAN satisfy — every option sold out — must not block the
  // cart, or the product becomes unbuyable for a reason the customer can't fix.
  function requiredGroupIsBlocking(group) {
    // Hidden by variant scope = not offered for the variant being bought, so
    // there is nothing to require. Without this, a group scoped to one variant
    // would block Add to cart on every other variant.
    if (group.hidden) return false;

    const selectable = qsa('[data-optional-checkbox]', group).filter((cb) => !cb.disabled);
    if (!selectable.length) return false;
    return !selectable.some((cb) => cb.checked);
  }

  function firstUnsatisfiedRequiredGroup() {
    return requiredOptionalGroups().find(requiredGroupIsBlocking) || null;
  }

  function setRequiredGroupError(group, on) {
    group.classList.toggle('is-required-error', !!on);
    const msg = qs('[data-optional-required-msg]', group);
    if (msg) msg.hidden = !on;
  }

  function flagRequiredGroup(group) {
    setRequiredGroupError(group, true);

    // Open the dropdown first — scrolling to a collapsed group shows the
    // customer a message and nothing to act on.
    const trigger = qs('[data-optional-dropdown-trigger]', group);
    const panel = qs('[data-optional-dropdown-panel]', group);
    if (trigger && panel && panel.hidden) {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      const icon = qs('.optional-dropdown-trigger__icon', trigger);
      if (icon) icon.textContent = '−';
    }

    try {
      group.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (_) {
      group.scrollIntoView();
    }
  }

  // A PDP also renders product CARDS (related products, recommendations, the
  // Ultrapure comparison cards), each with its own /cart/add form and quick-add
  // button. Those add a DIFFERENT product, so a required group here must never
  // block them.
  const OTHER_PRODUCT_SCOPES =
    '.product-item, .ultrapure-v2__product, .product-card, [data-product-card], .mini-cart, .predictive-search';

  // Would this click add the MAIN product to the cart? Payment buttons are
  // excluded (toggleDynamicCheckoutForInclusions hides them anyway) and
  // quantity steppers are type="button", so they never match.
  function isAtcControl(target) {
    const el = target && target.closest && target.closest('button, input[type="submit"]');
    if (!el) return false;
    if (el.closest('.shopify-payment-button')) return false;
    if (el.closest(OTHER_PRODUCT_SCOPES)) return false;

    // Covers both the in-page form and the sticky bar, which renders its own
    // product form for the same product.
    if (el.matches('[name="add"], .product-form__submit')) return true;
    if (!el.closest('form[action*="/cart/add"]')) return false;
    return el.matches('button[type="submit"], input[type="submit"]');
  }

  function initRequiredOptionalGroups() {
    if (!requiredOptionalGroups().length) return;

    document.addEventListener('click', function (e) {
      if (!isAtcControl(e.target)) return;

      const group = firstUnsatisfiedRequiredGroup();
      if (!group) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      flagRequiredGroup(group);
      dlog('ATC blocked: required group has no selection', group.dataset.groupKey);
    }, true);

    // Enter in the quantity field, or a theme that submits programmatically,
    // never produces a click on the button.
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (form.closest(OTHER_PRODUCT_SCOPES)) return;

      const group = firstUnsatisfiedRequiredGroup();
      if (!group) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      flagRequiredGroup(group);
      dlog('ATC submit blocked: required group has no selection', group.dataset.groupKey);
    }, true);

    // Clear the warning as soon as the group is satisfied.
    document.addEventListener('change', function (e) {
      const cb = e.target && e.target.closest && e.target.closest('[data-optional-checkbox]');
      if (!cb) return;

      const group = cb.closest('[data-optional-group][data-required="true"]');
      if (group && !requiredGroupIsBlocking(group)) setRequiredGroupError(group, false);
    });
  }


  /* =========================
     OPTIONAL GROUPS: VARIANT SCOPE (PDP)

     Inclusions attach to the PRODUCT (product.metafields.custom.
     optional_product_group*), so every group Liquid renders is on the page for
     all variants. A group carrying data-for-variants is shown only while one of
     those variants is selected — e.g. a free item offered on the 16L Superoo
     but not the 12L or 20L.

     Hiding also CLEARS the group's selection, and that is what keeps the rest
     of this file variant-agnostic: the cart payload and the running total both
     collect only checked boxes, so neither needs to know about variant scope.
     The one place that does is requiredGroupIsBlocking, above.
  ========================= */

  function currentMainVariantId() {
    return safeId(getMainVariantId(findForm()));
  }

  // The theme does NOT keep its hidden <select name="id"> in sync on a
  // multi-variant product — the same trap setupTotalRow documents below: the
  // selectedIndex never moves, so reading it after the customer switches size
  // returns whatever the page was FIRST rendered with. Trusting it made a
  // 16L-scoped group stay visible on 20L, because the stale read still said
  // 16L. variant:changed carries the real variant, so once we have seen one we
  // stop believing the DOM inputs entirely.
  let scopeVariantId = null;
  let sawVariantChanged = false;

  function initialScopeVariantId() {
    // ?variant= is authoritative on first paint, and on a plain URL the form
    // still agrees with the server-rendered selection — staleness only starts
    // once the customer interacts.
    try {
      const fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return safeId(fromUrl);
    } catch (_) { /* no URLSearchParams: fall through */ }
    return currentMainVariantId();
  }

  function groupAppliesToVariant(group, variantId) {
    const raw = (group.dataset.forVariants || '').trim();
    if (!raw) return true;      // unscoped — every variant
    if (!variantId) return true; // variant unknown: fail open rather than hide
    return raw.split(',').some((id) => id.trim() === String(variantId));
  }

  function applyVariantScope(variantId) {
    if (variantId) scopeVariantId = variantId;
    const id = scopeVariantId;

    qsa('[data-optional-group][data-for-variants]').forEach((group) => {
      const applies = groupAppliesToVariant(group, id);
      if (group.hidden === !applies) return; // already in the right state

      group.hidden = !applies;

      // Coming back into view: restore the pre-ticked default, since the tick
      // was cleared on the way out. Without this, landing on 12L and switching
      // to 16L would show the free cap unticked.
      if (applies) {
        applyPreselect(group);
        syncOptionalGroupUi(group);
        return;
      }

      qsa('[data-optional-checkbox]', group).forEach((cb) => {
        if (!cb.checked) return;
        cb.checked = false;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      });

      group.querySelectorAll('.optional-dropdown-item').forEach(function (row) {
        row.classList.remove('is-selected');
      });
      syncOptionalDropdownTrigger(group);
      setRequiredGroupError(group, false);

      if (typeof window.__mfw_syncOptionalVerticalAddon === 'function') {
        qsa('[data-optional-addon]', group).forEach(window.__mfw_syncOptionalVerticalAddon);
      }
    });

    // Mandatory sections scope the same way, but they cannot be handled by
    // clearing a selection the way optional groups are: a mandatory section
    // ALWAYS has a selected tile. So the three readers that walk
    // .mandatory-section — validateMandatory, sumSelectedMandatory and
    // getSelectedMandatoryItems — each skip a hidden one instead.
    let mandatoryChanged = false;
    qsa('.mandatory-section[data-for-variants]').forEach((section) => {
      const applies = groupAppliesToVariant(section, id);
      if (section.hidden === !applies) return;
      section.hidden = !applies;
      mandatoryChanged = true;
    });

    if (mandatoryChanged && typeof window.__mfwRecomputeTotal === 'function') {
      window.__mfwRecomputeTotal();
    }
  }

  /* =========================
     OPTIONAL GROUPS: PRE-SELECTED (PDP)

     A group flagged `preselected` opens with its first purchasable option
     already ticked — an unticked free gift is one most customers never notice.
     Liquid renders the `checked` attribute, so first paint is already correct;
     this brings the surrounding UI into line (dropdown trigger label, the
     vertical ADD/REMOVE button) and puts the tick back when a variant-scoped
     group is shown again after being cleared on the way out.
  ========================= */

  function applyPreselect(group) {
    if (!group || group.dataset.preselected !== 'true' || group.hidden) return;

    const boxes = qsa('[data-optional-checkbox]', group);
    if (boxes.some((cb) => cb.checked)) return; // customer's choice already stands

    const first = boxes.find((cb) => !cb.disabled);
    if (!first) return;

    first.checked = true;
    const row = first.closest('.optional-dropdown-item');
    if (row) row.classList.add('is-selected');
    first.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncOptionalGroupUi(group) {
    syncOptionalDropdownTrigger(group);
    if (typeof window.__mfw_syncOptionalVerticalAddon === 'function') {
      qsa('[data-optional-addon]', group).forEach(window.__mfw_syncOptionalVerticalAddon);
    }
  }

  function initOptionalPreselect() {
    const groups = qsa('[data-optional-group][data-preselected="true"]');
    if (!groups.length) return;

    groups.forEach((group) => {
      applyPreselect(group);
      syncOptionalGroupUi(group);
    });
  }

  function initVariantScope() {
    const scoped = qsa('[data-optional-group][data-for-variants], .mandatory-section[data-for-variants]');
    if (!scoped.length) return;

    scopeVariantId = initialScopeVariantId();
    applyVariantScope();

    // The theme fires variant:changed last, after rebuilding its own markup,
    // and hands over the full variant object — same hook setupTotalRow uses.
    // This is the only source we trust once it has spoken.
    document.addEventListener('variant:changed', (e) => {
      const id = safeId(e.detail?.variant?.id);
      if (!id) return;
      sawVariantChanged = true;
      applyVariantScope(id);
    });

    // Fallback for a page where nothing fires variant:changed. Read the element
    // that actually changed rather than re-querying the form, and stand down for
    // good once the theme has proved it fires the real event — the theme also
    // fires `change` on its stale no-js select, which would otherwise clobber
    // the correct variant a moment after we got it.
    document.addEventListener('change', (e) => {
      if (sawVariantChanged) return;
      if (!e.target?.matches?.('select[name="id"], input[name="id"]')) return;
      applyVariantScope(safeId(e.target.value));
    });
  }


  /* =========================
     TIME-BASED OPTIONS (PDP)
     - UI state only (selection)
  ========================= */
  function initTimeBasedOptions() {
    const groups = qsa('[data-tbo-group]');
    if (!groups.length) return;

    groups.forEach((group) => {
      const btns = qsa('[data-tbo-btn]', group);
      const panels = qsa('[data-tbo-panel]', group);
      if (!btns.length || !panels.length) return;

      const activate = (id) => {
        btns.forEach((b) => {
          const on = String(b.dataset.tboBtn) === String(id);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        panels.forEach((p) => {
          const on = String(p.dataset.tboPanel) === String(id);
          p.classList.toggle('is-active', on);
          if (on) p.removeAttribute('hidden');
          else p.setAttribute('hidden', '');
        });

        // ✅ AFTER switching panel, re-evaluate ATC disabled state
        const form = findForm();
        const atcBtn = findAddBtn(form);
        if (atcBtn) evaluateTboAndToggleAtc(atcBtn);
      };

      // Ensure a valid default (first rendered button)
      const first = btns[0];
      if (first) activate(first.dataset.tboBtn);

      // Click handling (single-select)
      btns.forEach((b) => {
        b.addEventListener('click', (e) => {
          e.preventDefault();
          activate(b.dataset.tboBtn);
        });
      });
    });
  }


  function getActiveTimeBasedVariantIds() {
    const group = qs('[data-tbo-group]');
    if (!group) return null;

    const activePanel = qs('.tbo-panel.is-active[data-tbo-panel]', group);
    if (!activePanel) return { optionId: '', variantIds: [], skippedTitles: [] };

    const optionId = activePanel.dataset.tboPanel || '';
    const cards = qsa('.tbo-card', activePanel);

    const variantIds = [];
    const skippedTitles = [];

    cards.forEach((card) => {
      const raw = card.dataset.tboVariantId; // may be missing now (sold out)
      const vid = Number(raw);

      if (Number.isFinite(vid) && vid > 0) {
        variantIds.push(vid);
      } else {
        const title = qs('.tbo-card-title', card)?.textContent?.trim() || '(unknown)';
        skippedTitles.push(title);
      }
    });

    return { optionId, variantIds, skippedTitles };
  }

  function isCardPurchasable(card, qty) {
    // If inventory is not tracked, allow
    const tracked = String(card.dataset.tracked || '') === 'true';
    if (!tracked) return true;

    const inv = parseInt(card.dataset.inventory || '0', 10) || 0;
    const back = String(card.dataset.allowsBackorder || '') === 'true';

    return inv >= qty || back;
  }

  function setAtcState(btn, disabled, reasonText) {
    if (!btn) return;

    // Save the original label ONCE so we can restore it later
    if (!btn.dataset.mfwAtcOriginalText) {
      // Prefer a nested label span if your theme uses one
      const labelNode =
        btn.querySelector('[data-add-to-cart-text]') ||
        btn.querySelector('.product-form__add-button-text') ||
        btn.querySelector('span') ||
        btn;

      btn.dataset.mfwAtcOriginalText = (labelNode.textContent || '').trim() || 'Add to cart';
    }

    const labelNode =
      btn.querySelector('[data-add-to-cart-text]') ||
      btn.querySelector('.product-form__add-button-text') ||
      btn.querySelector('span') ||
      btn;

    const newText = disabled
      ? (reasonText || 'Sold out')
      : btn.dataset.mfwAtcOriginalText;

    // Attributes
    if (disabled) {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('disabled');
      btn.setAttribute('aria-disabled', 'false');
    }

    // Classes (your requirement)
    btn.classList.toggle('button--disabled', !!disabled);
    btn.classList.toggle('button--primary', !disabled);

    // Optional marker class (used by the click hard-block)
    btn.classList.toggle('mfw-atc-disabled', !!disabled);

    // Label text
    if (labelNode) labelNode.textContent = newText;
  }


  function evaluateTboAndToggleAtc(btn) {
    const group = qs('[data-tbo-group]');
    if (!group) return;

    const qty = getQty();
    const activePanel = qs('.tbo-panel.is-active[data-tbo-panel]', group);
    if (!activePanel) return;

    const cards = qsa('.tbo-card', activePanel);

    const isSoldOutCard = (card) => {
      const hasId = !!card.dataset.tboVariantId; // missing when liquid marked sold out
      if (!hasId) return true;
      return !isCardPurchasable(card, qty);
    };

    const hasAnySoldOut = cards.some(isSoldOutCard);

    cards.forEach((card) => {
      card.classList.toggle('is-disabled', isSoldOutCard(card));
    });

    // ✅ THIS is the key line you commented out
    setAtcState(btn, hasAnySoldOut, 'Sold out');
  }


  function setupOptionalVerticalButtons() {
    // prevent double-binding if script runs twice (theme re-render, app inject, etc.)
    if (window.__mfw_optional_vertical_booted) return;
    window.__mfw_optional_vertical_booted = true;

    // Sync one card's button UI to match its hidden checkbox state
    function syncAddon(addon) {
      const checkbox = addon.querySelector('input[data-optional-checkbox]');
      const btn = addon.querySelector('[data-optional-toggle]');
      const card = addon.querySelector('.optional-vcard'); // ✅ define it
      if (!checkbox || !btn) return;

      const on = !!checkbox.checked;
      btn.classList.toggle('is-active', on);
      btn.textContent = on ? 'REMOVE' : 'ADD';

      addon.classList.toggle('is-selected', on);
      if (card) card.classList.toggle('is-selected', on);
    }



    // Click handler for ADD/REMOVE buttons (event delegation)
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-optional-toggle]');
      if (!btn) return;

      const addon = btn.closest('[data-optional-addon]');
      if (!addon) return;

      const checkbox = addon.querySelector('input[data-optional-checkbox]');
      if (!checkbox || checkbox.disabled) return;

      // Toggle checkbox (this is what your existing logic reads for adding to cart)
      checkbox.checked = !checkbox.checked;

      // Trigger any existing listeners (validation, etc.)
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));

      // Update button UI immediately
      syncAddon(addon);
    });

    // Initial sync on load (so ADD/REMOVE matches any pre-checked state)
    document
      .querySelectorAll('.optional-product-group--vertical [data-optional-addon]')
      .forEach(syncAddon);

    // Expose sync so your validation code can re-sync after it disables/unchecks items
    window.__mfw_syncOptionalVerticalAddon = syncAddon;
  }

  function setupTotalRow() {
    const root = document.querySelector('[data-product-inclusions]');
    if (!root) return;

    let lastTotalCents = null;

    // shared parent (where you moved the total row)
    const scope = root.closest('.product-form__buy-buttons') || document;

    // ✅ must query from scope, NOT root
    const row = scope.querySelector('[data-mfw-total-row]');
    const amt = scope.querySelector('[data-mfw-total]');

    if (!row || !amt) {
      dlog('TOTAL: missing row/amt', {
        hasRoot: !!root,
        hasScope: !!scope,
        row: !!row,
        amt: !!amt
      });
      return;
    }


    const formatMoney = (cents) => {
      const n = (Number(cents || 0) / 100);
      try {
        const s = (Number(cents || 0) / 100).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        return `$${s}`;
      } catch {
        return `$${n.toFixed(2)}`;
      }
    };

    /* ------------------------------------------------------------------------
       Main price, variant-aware.

       This used to read select[name="id"].options[selectedIndex] first and, for
       a multi-variant product, fall back to a hidden input[name="id"] that only
       exists on SINGLE-variant products — so on a product with real variants it
       could return the first variant's price forever, or 0, leaving the whole
       main product out of "Total including Add-ons".

       Two things make it reliable now:
         * the theme hands us the actual variant object on variant:changed
           (price already in cents) and that is trusted first;
         * every fallback resolves the id by .value and looks the price up in a
           rendered map, instead of trusting the hidden no-js <select>'s
           selectedIndex — the theme syncs that select by juggling the `selected`
           ATTRIBUTE, which does not reliably move selectedIndex.
       ------------------------------------------------------------------------ */
    let liveVariant = null;

    const variantPriceFromMaps = (idNum) => {
      // id/price pairs from product-buy-buttons.liquid
      // full variant JSON from product-variant-selector.liquid
      const sources = ['[data-mfw-variant-prices]', '[data-mfw-variants-json]'];
      for (const sel of sources) {
        const el = document.querySelector(sel);
        if (!el) continue;
        try {
          const arr = JSON.parse(el.textContent || '[]');
          const match = (Array.isArray(arr) ? arr : []).find(v => Number(v.id) === idNum);
          const cents = Number(match?.price);
          if (Number.isFinite(cents) && cents > 0) return cents;
        } catch (e) { /* malformed JSON: try the next source */ }
      }
      return 0;
    };

    const getSelectedVariantId = () => {
      const form = document.querySelector('form[action*="/cart/add"]');
      const raw =
        form?.querySelector('input[name="id"]')?.value ||
        form?.querySelector('select[name="id"]')?.value ||
        document.querySelector('input[name="id"]')?.value ||
        document.querySelector('select[name="id"]')?.value ||
        '';
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    const getMainCents = () => {
      const idNum = getSelectedVariantId();

      // 1) What the theme last told us — but only while it still agrees with the
      //    form, so a stale event can't outvote the real selection.
      if (liveVariant && (!idNum || Number(liveVariant.id) === idNum)) {
        const cents = Number(liveVariant.price);
        if (Number.isFinite(cents) && cents >= 0) return cents;
      }

      // 2) Resolve the selected id against the rendered price maps.
      if (idNum) {
        const cents = variantPriceFromMaps(idNum);
        if (cents > 0) return cents;
      }

      // 3) Last resort for first paint before any variant event has fired.
      const sel = document.querySelector('select[name="id"]');
      if (sel) {
        const opt = sel.options[sel.selectedIndex];
        const cents = parseInt(opt?.dataset?.priceCents || '0', 10) || 0;
        if (cents > 0) return cents;
      }

      return 0;
    };



    const getAddonCentsForCard = (card) => {
      // If user selected a different variant from dropdown inside this card, use that
      // A free ($0.00) inclusion is still active — key off the discount variant's
      // existence, not a positive amount.
      const hasInclusion = !!(card.dataset.discountVariantId || '').trim();
      const sel = card.querySelector('[data-optional-variant-select]');
      if (sel) {
        const opt = sel.options[sel.selectedIndex];
        const variantCents = parseInt(opt?.dataset?.priceCents || '0', 10) || 0;

        const disc = parseInt(card.dataset.discountPriceCents || '0', 10) || 0;
        return hasInclusion ? disc : variantCents;
      }

      // Otherwise: card default variant
      const base = parseInt(card.dataset.priceCents || '0', 10) || 0;
      const disc = parseInt(card.dataset.discountPriceCents || '0', 10) || 0;
      return hasInclusion ? disc : base;
    };

    const sumSelectedAddons = (scope) => {
      let sum = 0;
      scope.querySelectorAll('[data-optional-addon]').forEach((card) => {
        const cb = card.querySelector('input[data-optional-checkbox]');
        if (!cb || !cb.checked) return;
        sum += getAddonCentsForCard(card);
      });
      return sum;
    };

    const parseBool = (v) => {
      if (typeof v === 'boolean') return v;
      const raw = String(v ?? '').trim();
      if (!raw) return false;
      try { return JSON.parse(raw) === true; } catch { return raw.toLowerCase() === 'true'; }
    };

    function triggerAfterpayRefresh(totalCents) {
      const ap = document.querySelector('#product-Afterpay-wrapper');
      if (!ap) return;

      // Most Afterpay widgets expect dollars as a string
      const dollars = (Number(totalCents || 0) / 100).toFixed(2);

      ap.setAttribute('data-afterpay-amount', dollars);
      ap.dataset.afterpayAmount = dollars;

      try { document.dispatchEvent(new CustomEvent('afterpay:refresh')); } catch (e) {}

      try {
        if (window.afterpay && typeof window.afterpay.refresh === 'function') window.afterpay.refresh();
        if (window.Afterpay && typeof window.Afterpay.refresh === 'function') window.Afterpay.refresh();
      } catch (e) {}
    }





    /* syncMainDisplayedPrice was removed here.

       It replaced the product's headline price with the add-ons total, so the
       price shown for an inclusion product jumped to a multiple of itself when
       the quantity changed, and inclusion products behaved unlike every other
       product on the site. The total now lives only in its own row and the
       theme's price is left untouched. See git history if it's ever needed. */



    const recompute = () => {
      const qty = getQty();

      // IMPORTANT: use the shared scope (because you moved the total row)
      const scope = root.closest('.product-form__buy-buttons') || document;

      // Determine if mandatory exists + whether main should be excluded NOW.
      // computeExcludeMainNow handles both mandatory always-exclude and the
      // conditional optional exclude-when-selected case.
      const mandatoryWrap = scope.querySelector('.mandatory-inclusions');
      const excludeMain = computeExcludeMainNow(scope);

      // Count optional selections (within shared scope)
      const checkedOptionalCount = scope.querySelectorAll('input[data-optional-checkbox]:checked').length;

      // If mandatory exists, we should show total even if optional count is 0
      const shouldShow = !!mandatoryWrap || checkedOptionalCount > 0;

      if (!shouldShow) {
        row.hidden = true;
        return;
      }

      const mainCents = excludeMain ? 0 : getMainCents();
      const mandatoryCents = sumSelectedMandatory(scope);
      const optionalCents  = sumSelectedAddons(scope);

      const totalPerUnit = mainCents + mandatoryCents + optionalCents;
      const totalCents = totalPerUnit * qty;

      const formatted = formatMoney(totalCents);

      // Only touch DOM if it actually changed (reduces “theme reacts to price change” loops)
      if (amt.textContent !== formatted) amt.textContent = formatted;
      row.hidden = false;

      /* The product's own price is left alone on purpose. It stays the unit
         price the theme rendered; the quantity and any add-ons are reflected
         here in the total row only. Overwriting the headline price meant a
         shopper clicking + saw it jump to a multiple of itself, which reads as
         a price rise rather than a quantity change — and it made inclusion
         products behave unlike every other product on the site. */

      // ✅ Refresh Afterpay ONLY when total changes
      if (totalCents !== lastTotalCents) {
        lastTotalCents = totalCents;
        requestAnimationFrame(() => triggerAfterpayRefresh(totalCents));
      }

      if (DEBUG_MFW) {
        dlog('TOTAL recompute', { qty, excludeMain, mainCents, mandatory: mandatoryCents, optional: optionalCents, total: totalCents });
      }
    };



    const sumSelectedMandatory = (scope) => {
      let sum = 0;
      scope.querySelectorAll('.mandatory-section').forEach((section) => {
        if (section.hidden) return; // not shown for this variant, not in the total

        const selectedTile =
          section.querySelector('.mandatory-product-tile.is-selected') ||
          section.querySelector('.mandatory-product-tile');

        if (!selectedTile) return;

        const sel = selectedTile.querySelector('.mandatory-variant-select');
        let cents = 0;

        if (sel) {
          const opt = sel.options[sel.selectedIndex];
          cents = parseInt(opt?.dataset?.priceCents || '0', 10) || 0;
        } else {
          cents = parseInt(selectedTile.dataset.priceCents || '0', 10) || 0;
        }

        const disc = parseInt(selectedTile.dataset.discountPriceCents || '0', 10) || 0;
        // A free ($0.00) inclusion is still an ACTIVE inclusion. Key off the
        // discount variant's existence (matches the Liquid), not a positive
        // amount — otherwise a $0 inclusion falls back to the base price.
        const hasInclusion = !!(selectedTile.dataset.discountVariantId || '').trim();
        sum += hasInclusion ? disc : cents;
      });
      return sum;
    };


    // listen to changes
    // expose for other modules (mandatory tile click, etc.)
    window.__mfwRecomputeTotal = () => requestAnimationFrame(recompute);

    // quantity input: react to BOTH input + change
    document.addEventListener('input', (e) => {
      if (e.target?.name === 'quantity') window.__mfwRecomputeTotal();
    }, true);

    document.addEventListener('change', (e) => {
      if (e.target?.name === 'quantity') window.__mfwRecomputeTotal();
    }, true);

    // quantity +/- buttons: many themes update qty via clicks (no change event)
    document.addEventListener('click', (e) => {
      const qtyBtn =
        e.target.closest?.('[data-action="increase-quantity"]') ||
        e.target.closest?.('[data-action="decrease-quantity"]') ||
        e.target.closest?.('.quantity-selector__button') ||
        e.target.closest?.('.quantity__button') ||
        e.target.closest?.('[data-quantity-increase]') ||
        e.target.closest?.('[data-quantity-decrease]');

      if (qtyBtn) window.__mfwRecomputeTotal();
    }, true);

    // mandatory tile selection is click-based → trigger recompute
    document.addEventListener('click', (e) => {
      if (e.target.closest?.('.mandatory-product-tile')) {
        window.__mfwRecomputeTotal();
      }
    }, true);


    document.addEventListener('change', (e) => {
      if (
        e.target.matches('select[name="id"]') ||
        e.target.matches('input[name="id"]') ||
        e.target.matches('input[data-optional-checkbox]') ||
        e.target.matches('[data-optional-variant-select]') ||
        e.target.matches('.mandatory-variant-select') ||
        e.target.closest?.('.mandatory-product-tile')
      ) {
        recompute();
      }
    }, true);

    /* Variant switch. This is the authoritative hook: the theme fires
       variant:changed (bubbling, with the full variant object) as the last step
       of its own update, after it has rebuilt .price-list from scratch. So
       recomputing here both picks up the new variant price and re-applies our
       total over the markup the theme just replaced.

       The theme also fires a bubbling `change` on its hidden no-js <select>,
       which the listener above catches — but that only re-ran the old numbers
       while getMainCents was reading a selectedIndex the theme never moved. */
    document.addEventListener('variant:changed', (e) => {
      liveVariant = (e.detail && e.detail.variant) || null;
      requestAnimationFrame(recompute);
    });

    // for your vertical ADD/REMOVE buttons (they toggle checkbox via JS)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-optional-toggle]')) {
        requestAnimationFrame(recompute);
      }
    }, true);

    recompute();
  }




  /* =========================
     MANDATORY GROUPS (PDP)
  ========================= */
  let validateMandatory = () => true;

  function initMandatoryGroups() {
    const sections = qsa('.mandatory-section');
    if (!sections.length) return;

    validateMandatory = function () {
      const qty = getQty();
      let okAll = true;

      sections.forEach((section) => {
        // Hidden by variant scope: not part of this variant's package, so it
        // must not be able to fail validation and block Add to cart.
        if (section.hidden) return;

        let hasValid = false;

        qsa('.mandatory-product-tile', section).forEach((tile) => {
          const tracked = String(tile.dataset.tracked || '') === 'true';
          const stocksFromSuppliers = String(tile.dataset.stocksFromSuppliers || '') === 'true';

          const inv = parseInt(tile.dataset.inventory || '0', 10);
          const back = tile.dataset.allowsBackorder === 'true';

          const ok = !tracked
            ? stocksFromSuppliers
            : (inv >= qty || back);

          tile.classList.toggle('is-disabled', !ok);
          if (ok) hasValid = true;
        });


        if (!hasValid) okAll = false;
      });

      return okAll;
    };

    document.addEventListener('change', (e) => {
      if (e.target?.name === 'quantity') validateMandatory();
    });
  }

  function initUfeBridge(form, btn) {
    let inFlight = false;

    async function sleep(ms) {
      return new Promise(r => setTimeout(r, ms));
    }

    btn.addEventListener('click', async () => {
      if (inFlight) return;

      const qty = getQty();
      const mainId = safeId(getMainVariantId(form));
      if (!mainId) return;

      // Snapshot cart BEFORE UFE runs
      let beforeQty = 0;
      try {
        const cartBefore = await fetchCart();
        const beforeItem = cartBefore.items.find(i => Number(i.variant_id) === Number(mainId));
        beforeQty = beforeItem ? Number(beforeItem.quantity || 0) : 0;
      } catch {
        // If cart.js fails, bail quietly—don’t break UFE.
        return;
      }

      inFlight = true;

      try {
        // Poll for UFE add to land
        let addedQty = 0;

        for (let attempt = 0; attempt < 12; attempt++) {
          await sleep(350);

          const cartAfter = await fetchCart();
          const afterItem = cartAfter.items.find(i => Number(i.variant_id) === Number(mainId));
          const afterQty = afterItem ? Number(afterItem.quantity || 0) : 0;

          if (afterQty > beforeQty) {
            addedQty = afterQty - beforeQty;
            break;
          }
        }

        if (addedQty <= 0) return;

        // Build inclusions using the qty that actually got added by UFE
        const mandatoryItems = getSelectedMandatoryItems(addedQty, mainId);

        const optionalItems = [];
        qsa('[data-optional-group]').forEach((group) => {
          qsa('[data-optional-addon]', group).forEach((block) => {
            const cb = qs('[data-optional-checkbox]', block);
            if (!cb || !cb.checked) return;

            const addonId = safeId(cb.dataset.discountVariantId) || safeId(cb.dataset.variantId);
            if (!addonId) return;

            const baseVariantId = safeId(cb.dataset.baseVariantId || cb.dataset.variantId || block.dataset.variantId);
            const discountVariantId = safeId(cb.dataset.discountVariantId || block.dataset.discountVariantId);
            const inclusionProductId = safeId(cb.dataset.productId || block.dataset.productId);
            const parentProductId = safeId(cb.dataset.parentProductId || block.dataset.parentProductId);
            const originalPriceCents = toCents(cb.dataset.priceCents || block.dataset.priceCents);
            const discountedPriceCents = toCents(cb.dataset.discountPriceCents || block.dataset.discountPriceCents) || originalPriceCents;

            optionalItems.push({
              id: Number(addonId),
              quantity: addedQty,
              properties: buildInclusionProperties({
                groupId: mainId,
                inclusionType: 'optional',
                mainProductId: parentProductId,
                mainVariantId: mainId,
                inclusionProductId,
                baseVariantId,
                discountVariantId,
                originalPriceCents,
                discountedPriceCents
              })
            });
          });
        });

        const itemsToAdd = [...mandatoryItems, ...optionalItems];
        if (!itemsToAdd.length) return;

        await postAddItems(itemsToAdd);
      } finally {
        inFlight = false;
      }
    }, true);
  }


  /* =========================
     PDP: ADD TO CART (BATCH)
     - If Time-Based Options exists: add ONLY selected option products
     - Else: existing main + optionals behavior
  ========================= */
  function initAddToCart() {
    const form = findForm();
    if (!form) {
      return;
    }

    const btn = findAddBtn(form);
    if (!btn) {
      return;
    }

    const hasTBO = !!qs('[data-tbo-group]');
    const hasUFE = hasUFEOnPage();
    const hasInc = hasInclusionsOnPage();

    // ✅ HARD RULE:
    // If TBO exists, we ALWAYS exclude main product and ALWAYS take over ATC.
    // UFE is ignored in this mode, because we are not adding the main product at all.
    //
    // For the UFE-bridge / takeover decision below we need to know whether
    // exclude-main is even *possible* on this page (not just whether it's
    // active right now). Mandatory's flag is "always", but optional's flag
    // is "conditional on selection" — and at init time the user hasn't
    // selected anything yet. We must take over ATC if either system could
    // ever exclude main this session, so that the click handler can re-
    // evaluate dynamically. Actual cart logic uses computeExcludeMainNow().
    const excludeMainPossible = hasTBO
      || isMandatoryExcludeMain()
      || hasOptionalExcludeMainFlag();

    // If UFE exists and there are no inclusions UI, let UFE do its thing.
    // (Only applies when NOT in TBO mode.)
    if (!hasTBO && hasUFE && !hasInc) {
      return;
    }

    // If UFE exists AND exclude-main is not possible at all,
    // do NOT take over ATC. Bridge after UFE adds main.
    // (Only applies when NOT in TBO mode.)
    if (!hasTBO && hasUFE && !excludeMainPossible) {
      initUfeBridge(form, btn);
      return;
    }

    // Otherwise (no UFE OR exclude-main is possible OR TBO present),
    // MFW will handle ATC.

    // ✅ Evaluate immediately on load (if TBO exists)
    if (hasTBO) {
      evaluateTboAndToggleAtc(btn);

      // Re-evaluate when quantity changes
      document.addEventListener('change', (e) => {
        if (e.target?.name === 'quantity') evaluateTboAndToggleAtc(btn);
      });
    }

    btn.addEventListener(
      'click',
      async (e) => {
        dlog('PDP ATC click: fired');

        // Prevent ANY default behavior (native submit / theme handlers)
        e.preventDefault();
        e.stopImmediatePropagation();

        // HARD BLOCK if we disabled it due to sold-out item(s)
        if (hasTBO && (btn.disabled || btn.classList.contains('mfw-atc-disabled'))) {
          dlog('PDP ATC: blocked - package has sold out item(s)');
          return;
        }

        if (btn.dataset.mfwHandled === '1') {
          dlog('PDP ATC: already handled (dataset lock)');
          return;
        }

        btn.dataset.mfwHandled = '1';
        window.__mfw_handling_atc = true;

        // 2026-06-19: our native ATC is taking over — drop any pending
        // optional-only snapshot so flushPendingOptionals() (in this IIFE,
        // bottom of file) doesn't double-add on the next page load. See
        // the shim block at end of file for the back-order rationale.
        try {
          sessionStorage.removeItem('mfw_pending_optionals');
          sessionStorage.removeItem('mfw_optional_draft');
        } catch (_) {}

        try {
          const qty = getQty();
          dlog('PDP ATC: qty', qty);

          // -------------------------
          // ✅ TIME-BASED OVERRIDE
          // -------------------------
          if (hasTBO) {
            // Extra safety: re-check right before add
            evaluateTboAndToggleAtc(btn);
            if (btn.disabled || btn.classList.contains('mfw-atc-disabled')) {
              dlog('PDP ATC (TBO): blocked after re-eval - sold out detected');
              btn.dataset.mfwHandled = '0';
              window.__mfw_handling_atc = false;
              return;
            }

            const tbo = getActiveTimeBasedVariantIds();
            dlog('PDP ATC (TBO): active option + ids', tbo);

            if (!tbo || !tbo.variantIds || !tbo.variantIds.length) {
              dlog('PDP ATC (TBO): NO variant ids found → STOP (do not add main)');
              btn.dataset.mfwHandled = '0';
              window.__mfw_handling_atc = false;
              alert('No products found for the selected package option.');
              return;
            }

            const { optionId, variantIds } = tbo;

            // Deduplicate (if duplicates exist, we multiply quantity)
            const counts = new Map();
            variantIds.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));

            const items = Array.from(counts.entries()).map(([id, count]) => ({
              id: Number(id),
              quantity: qty * Number(count),
              properties: {
                _mfw_tbo: 'true',
                _mfw_tbo_option: String(optionId || '1')
              }
            }));

            dlog('PDP ATC (TBO): POST /cart/add.js items', items);

            const added = await postAddItems(items);
            dlog('PDP ATC (TBO): add.js response', added);

            dlog('PDP ATC (TBO): success → redirect /cart');
            location.href = '/cart';
            return;
          }

          // -------------------------
          // ✅ DEFAULT EXISTING BEHAVIOR
          // -------------------------
          if (!validateMandatory()) {
            dlog('PDP ATC: mandatory validation failed');
            btn.dataset.mfwHandled = '0';
            window.__mfw_handling_atc = false;
            return;
          }

          // Re-evaluate exclude-main at CLICK time (not init time): mandatory's
          // flag is static, but optional's "conditional exclude" semantic
          // depends on whether the user has actually selected any addon in a
          // flagged group right now.
          const excludeMain = computeExcludeMainNow();
          dlog('PDP ATC (DEFAULT): excludeMain at click =', excludeMain);

          if (excludeMain) {
            // Add selected mandatory + selected optional inclusions (NO main product)
            const groupToken = `MAND_ONLY_${Date.now()}`;

            const mandatoryOnlyItems = getSelectedMandatoryItems(qty, groupToken);

            const optionalItems = [];
            qsa('[data-optional-group]').forEach((group) => {
              qsa('[data-optional-addon]', group).forEach((block) => {
                const cb = qs('[data-optional-checkbox]', block);
                if (!cb || !cb.checked) return;

                const addonId = safeId(cb.dataset.discountVariantId) || safeId(cb.dataset.variantId);
                if (!addonId) return;

                const baseVariantId = safeId(
                  cb.dataset.baseVariantId || cb.dataset.variantId || block.dataset.variantId
                );
                const discountVariantId = safeId(
                  cb.dataset.discountVariantId || block.dataset.discountVariantId
                );
                const inclusionProductId = safeId(
                  cb.dataset.productId || block.dataset.productId
                );
                const parentProductId = safeId(
                  cb.dataset.parentProductId || block.dataset.parentProductId
                );
                const originalPriceCents = toCents(
                  cb.dataset.priceCents || block.dataset.priceCents
                );
                const discountedPriceCents =
                  toCents(cb.dataset.discountPriceCents || block.dataset.discountPriceCents) ||
                  originalPriceCents;

                optionalItems.push({
                  id: Number(addonId),
                  quantity: qty,
                  properties: buildInclusionProperties({
                    groupId: groupToken,
                    inclusionType: 'optional',
                    mainProductId: parentProductId,
                    mainVariantId: groupToken,
                    inclusionProductId,
                    baseVariantId,
                    discountVariantId,
                    originalPriceCents,
                    discountedPriceCents
                  })
                });
              });
            });

            let itemsToAdd = [...mandatoryOnlyItems, ...optionalItems];

            // Same cartridge enrolment as the main path below — this branch
            // ships the inclusions without the parent, but the cartridges among
            // them still deserve to renew.
            if (typeof window.mfwApplyCartridgeSubscription === 'function') {
              itemsToAdd = window.mfwApplyCartridgeSubscription(itemsToAdd, form);
            }

            if (!itemsToAdd.length) {
              dlog('PDP ATC (DEFAULT): excludeMain=true but no inclusions selected');
              btn.dataset.mfwHandled = '0';
              window.__mfw_handling_atc = false;
              alert('Please select the required items before adding to cart.');
              return;
            }

            dlog('PDP ATC (DEFAULT): excludeMain=true → POST /cart/add.js items', itemsToAdd);
            await postAddItems(itemsToAdd);

            dlog('PDP ATC (DEFAULT): success → redirect /cart');
            location.href = '/cart';
            return;
          }

          // excludeMain=false → normal behavior: main + mandatory + optional
          const mainId = safeId(getMainVariantId(form));
          if (!mainId) {
            dlog('PDP ATC: NO main variant id');
            btn.dataset.mfwHandled = '0';
            window.__mfw_handling_atc = false;
            return;
          }

          const optionalItems = [];
          qsa('[data-optional-group]').forEach((group) => {
            qsa('[data-optional-addon]', group).forEach((block) => {
              const cb = qs('[data-optional-checkbox]', block);
              if (!cb || !cb.checked) return;

              const addonId = Number(cb.dataset.discountVariantId || cb.dataset.variantId);
              if (!addonId) return;
              const baseVariantId = safeId(cb.dataset.baseVariantId || cb.dataset.variantId || block.dataset.variantId);
              const discountVariantId = safeId(cb.dataset.discountVariantId || block.dataset.discountVariantId);
              const inclusionProductId = safeId(cb.dataset.productId || block.dataset.productId);
              const parentProductId = safeId(cb.dataset.parentProductId || block.dataset.parentProductId);
              const originalPriceCents = toCents(cb.dataset.priceCents || block.dataset.priceCents);
              const discountedPriceCents = toCents(cb.dataset.discountPriceCents || block.dataset.discountPriceCents) || originalPriceCents;

              optionalItems.push({
                id: addonId,
                quantity: qty,
                properties: buildInclusionProperties({
                  groupId: mainId,
                  inclusionType: 'optional',
                  mainProductId: parentProductId,
                  mainVariantId: mainId,
                  inclusionProductId,
                  baseVariantId,
                  discountVariantId,
                  originalPriceCents,
                  discountedPriceCents
                })
              });
            });
          });

          const mandatoryItems = getSelectedMandatoryItems(qty, mainId);

          // Subscription support: if the product form carries a selling_plan
          // (rendered by product-buy-buttons.liquid for cartridge auto-renewal),
          // attach it to the main line so subscription-only products can be added.
          const mainItem = { id: Number(mainId), quantity: qty };
          const sellingPlanEl = form.querySelector('[name="selling_plan"]');
          const sellingPlanId = sellingPlanEl && sellingPlanEl.value ? Number(sellingPlanEl.value) : 0;
          if (sellingPlanId) mainItem.selling_plan = sellingPlanId;

          let items = [
            mainItem,
            ...mandatoryItems,
            ...optionalItems
          ];

          // Cartridge Replacement Program: enrol this cart's cartridges into
          // auto-renewal when the opt-in is ticked. Defined in assets/custom.js;
          // returns the array unchanged when absent or unticked, so this is a
          // no-op on every other product.
          if (typeof window.mfwApplyCartridgeSubscription === 'function') {
            items = window.mfwApplyCartridgeSubscription(items, form);
          }

          dlog('PDP ATC (DEFAULT): POST /cart/add.js items', items);

          await postAddItems(items);

          dlog('PDP ATC (DEFAULT): success → redirect /cart');
          location.href = '/cart';

        } catch (err) {
          dlog('PDP ATC: ERROR', err);
          btn.dataset.mfwHandled = '0';
          window.__mfw_handling_atc = false;
          alert('Unable to add the selected package to cart. Check console for [MFW] CART/ADD FAILED details.');
        }
      },
      true // capture
    );
  }


  function isMandatoryInclusion(item) {
    return item.properties && String(item.properties._mfw_user_selected) === 'true';
  }


  /* =========================
     CART SYNC (UNCHANGED)
  ========================= */
 function initCartBatchSync() {
  if (!location.pathname.startsWith('/cart')) return;
  if (window.__mfw_cart_batch_sync_booted) return;
  window.__mfw_cart_batch_sync_booted = true;

  let busy = false;

  async function handleQtyChange(line, desiredQty) {
    if (busy) return;
    busy = true;

    try {
      const cart = await fetchCart();
      const anchor = cart.items[line - 1];
      if (!anchor) return location.reload();

      const updates = {};
      const isAddon = !!anchor.properties?._mfw_group;

      let desired = clampQty(desiredQty);

      // An inclusion cannot outnumber the product it came with. Nothing stopped
      // a customer taking one Superoo and then winding its free cap up to 40 in
      // the cart — the add-to-cart path matches quantities, but the cart line is
      // editable afterwards. Clamp to the host line; if the host is gone the
      // orphan sweep removes this line anyway, and an exclude-main group has no
      // host line to clamp against (its _mfw_group is a token, not a variant id).
      if (isAddon) {
        const hostVariantId = String(anchor.properties._mfw_group);
        const host = cart.items.find(
          (i) => !i.properties?._mfw_group && String(i.variant_id) === hostVariantId
        );
        if (host) desired = Math.min(desired, host.quantity);
      }

      const delta = desired - anchor.quantity;

      // Always update the anchor line item
      updates[anchor.key] = desired;

      // ✅ Only sync when the anchor is the MAIN product
      if (!isAddon) {
        const groupId = String(anchor.variant_id);

        const groupAddons = cart.items.filter(
          (i) => i.properties && String(i.properties._mfw_group) === groupId
        );

        // ✅ NEW RULE:
        // If MAIN product is removed (qty = 0), remove all grouped addons too.
        if (desired === 0) {
          groupAddons.forEach((i) => {
            updates[i.key] = 0;
          });
        } else {
          // Otherwise, keep the existing delta sync behavior
          groupAddons.forEach((i) => {
            updates[i.key] = clampQty(i.quantity + delta);
          });
        }
      }


      await postUpdate(updates);
      location.reload();
    } finally {
      busy = false;
    }
  }

  // PLUS/MINUS buttons
  document.addEventListener(
    'click',
    async (e) => {
      const btn =
        e.target.closest?.('[data-action="increase-quantity"]') ||
        e.target.closest?.('[data-action="decrease-quantity"]');

      if (!btn) return;

      const line = parseInt(btn.dataset.line, 10);
      const desiredQty = parseInt(btn.dataset.quantity, 10);

      if (!line || Number.isNaN(desiredQty)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      await handleQtyChange(line, desiredQty);
    },
    true
  );

  // Manual qty input edits
  document.addEventListener(
    'change',
    async (e) => {
      const input = e.target;

      if (!input) return;
      if (!(input.matches('input[name="updates[]"]') || input.dataset?.line)) return;

      const line = parseInt(input.dataset.line, 10);
      const desiredQty = parseInt(input.value, 10);

      if (!line || Number.isNaN(desiredQty)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      await handleQtyChange(line, desiredQty);
    },
    true
  );
}


  /* =========================
    TOOLTIPS (PDP + Cart)
  ========================= */
  function initTooltips() {
    const WRAP_SEL = '.mfw-tooltip, [data-mfw-tooltip]';
    const wrappers = () => qsa(WRAP_SEL);

    function closeAll() {
      wrappers().forEach(w => {
        w.classList.remove('is-open');
        const btn = qs('.mfw-tooltip-button', w);
        const panel = qs('.mfw-tooltip-panel', w);
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.setAttribute('aria-hidden', 'true');
      });
    }

    document.addEventListener('click', (e) => {
      const btn = e.target.closest?.('.mfw-tooltip-button');
      const closeBtn = e.target.closest?.('.mfw-tooltip-close');
      const wrapper = e.target.closest?.(WRAP_SEL);

      if (closeBtn) {
        e.preventDefault();
        const w = closeBtn.closest(WRAP_SEL);
        if (!w) return;
        w.classList.remove('is-open');
        const b = qs('.mfw-tooltip-button', w);
        const p = qs('.mfw-tooltip-panel', w);
        if (b) b.setAttribute('aria-expanded', 'false');
        if (p) p.setAttribute('aria-hidden', 'true');
        return;
      }

      if (btn) {
        e.preventDefault();

        const w = btn.closest(WRAP_SEL);
        if (!w) return;

        const isOpen = w.classList.contains('is-open');
        closeAll();

        if (!isOpen) {
          w.classList.add('is-open');
          const panel = qs('.mfw-tooltip-panel', w);
          btn.setAttribute('aria-expanded', 'true');
          if (panel) panel.setAttribute('aria-hidden', 'false');

          requestAnimationFrame(() => repositionTooltip(w));
          setTimeout(() => repositionTooltip(w), 50);
        }
        return;
      }

      if (!wrapper) closeAll();
    }, true);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });

    function repositionTooltip(w) {
      const panel = qs('.mfw-tooltip-panel', w);
      if (!panel) return;

      const boundary =
        w.closest('.optional-product-group') ||
        w.closest('.mandatory-section') ||
        w.closest('.product__info') ||
        w.closest('.product-form') ||
        w.closest('.product-form__info') ||
        document.documentElement;

      const boundaryRect = boundary.getBoundingClientRect();
      const pad = 12;

      panel.style.setProperty('--mfw-shift-x', '0px');

      const maxW = Math.max(180, Math.min(340, Math.floor(boundaryRect.width - pad * 2)));
      panel.style.maxWidth = `${maxW}px`;

      const rect = panel.getBoundingClientRect();

      let shift = 0;
      const overflowRight = rect.right - (boundaryRect.right - pad);
      const overflowLeft  = (boundaryRect.left + pad) - rect.left;

      if (overflowRight > 0) shift -= overflowRight;
      if (overflowLeft > 0) shift += overflowLeft;

      panel.style.setProperty('--mfw-shift-x', `${shift}px`);

      const placeArrow = () => {
        const iconBtn = qs('.mfw-tooltip-button', w);
        if (!iconBtn) return;

        const iconRect = iconBtn.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const iconCenterX = (iconRect.left + iconRect.right) / 2;

        let arrowX = iconCenterX - panelRect.left - 6;
        const minX = 12;
        const maxX = Math.max(minX, panelRect.width - 24);

        arrowX = Math.max(minX, Math.min(maxX, arrowX));
        panel.style.setProperty('--mfw-arrow-x', `${arrowX}px`);
      };

      requestAnimationFrame(placeArrow);
    }

    if (window.__mfw_tooltips_resize_bound) return;
    window.__mfw_tooltips_resize_bound = true;

    window.addEventListener('resize', () => {
      wrappers().forEach(w => {
        if (w.classList.contains('is-open')) repositionTooltip(w);
      });
    });
  }

  function initMandatoryTileSelection() {
    const sections = qsa('.mandatory-section');
    if (!sections.length) return;

    sections.forEach((section) => {
      const tiles = qsa('.mandatory-product-tile', section);
      const input = qs('.mandatory-section-input', section);

      if (!tiles.length || !input) return;

      tiles.forEach((tile) => {
        tile.addEventListener('click', () => {
          if (tile.classList.contains('is-disabled')) return;

          tiles.forEach(t => t.classList.remove('is-selected'));
          tile.classList.add('is-selected');
          input.value = tile.dataset.discountVariantId || tile.dataset.variantId;
        });

        tile.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          if (tile.classList.contains('is-disabled')) return;
          e.preventDefault();
          tile.click();
        });


      });

      // Default select the first non-disabled tile
      const firstValid = tiles.find(t => !t.classList.contains('is-disabled'));
      if (firstValid) {
        firstValid.classList.add('is-selected');
        input.value = firstValid.dataset.discountVariantId || firstValid.dataset.variantId;
      }
    });
  }

  // Mandatory "Dropdown" layout. The tiles inside the panel are byte-for-byte
  // the grid's tiles, so selection, the running total and variant pricing keep
  // running through the existing handlers; this only adds the open/close
  // chrome and keeps the trigger label showing the current pick.
  function initMandatoryDropdowns() {
    const shells = qsa('[data-mandatory-dropdown]');
    if (!shells.length) return;

    /* Same formatting as the one further up the file, which is scoped to the
       function it lives in and not reachable from here. */
    const money = (cents) => {
      const n = Number(cents || 0) / 100;
      try {
        return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } catch (e) {
        return '$' + n.toFixed(2);
      }
    };

    function syncLabel(shell) {
      const text = qs('[data-mandatory-dropdown-text]', shell);
      if (!text) return;

      const tile =
        qs('.mandatory-product-tile.is-selected', shell) ||
        qs('.mandatory-product-tile', shell);
      const title = tile && qs('.mandatory-product-title', tile);
      if (!title) { text.textContent = 'Select an option'; return; }

      /* The collapsed trigger named the choice but not its cost, so the only
         way to see what an option adds was to open the panel again. The price
         comes from the tile's own data rather than from its rendered markup,
         which carries a compare-at price alongside and would read wrongly.

         A zero stays silent: an option included in the system price does not
         need "+ $0.00" after it. */
      const cents = Number(
        tile.getAttribute('data-discount-price-cents') ||
        tile.getAttribute('data-price-cents') || 0
      );
      text.textContent = title.textContent.trim() +
        (cents > 0 ? '  +' + money(cents) : '');
    }

    function setOpen(shell, open) {
      const trigger = qs('[data-mandatory-dropdown-trigger]', shell);
      const panel = qs('[data-mandatory-dropdown-panel]', shell);
      if (!trigger || !panel) return;

      panel.hidden = !open;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      const icon = qs('.optional-dropdown-trigger__icon', trigger);
      if (icon) icon.textContent = open ? '−' : '+';
    }

    shells.forEach(syncLabel);

    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-mandatory-dropdown-trigger]');
      if (trigger) {
        const shell = trigger.closest('[data-mandatory-dropdown]');
        setOpen(shell, trigger.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const tile = e.target.closest('.mandatory-product-tile');
      const shell = tile && tile.closest('[data-mandatory-dropdown]');
      if (shell) {
        // The variant <select> sits inside the tile — using it must not
        // collapse the panel out from under the customer.
        if (e.target.closest('.mandatory-variant-select')) return;
        if (tile.classList.contains('is-disabled')) return;

        // initMandatoryTileSelection's own tile listener has already run by the
        // time this bubbles up to document, so is-selected is current.
        syncLabel(shell);
        setOpen(shell, false);
        return;
      }

      if (!e.target.closest('[data-mandatory-dropdown]')) {
        shells.forEach(function (s) { setOpen(s, false); });
      }
    });

    document.addEventListener('change', function (e) {
      const shell = e.target.closest && e.target.closest('[data-mandatory-dropdown]');
      if (shell) syncLabel(shell);
    });
  }

  function getSelectedMandatoryItems(qty, groupId) {
    const items = [];

    qsa('.mandatory-section').forEach((section) => {
      if (section.hidden) return; // not shown for this variant, not in the cart

      const selectedTile =
        qs('.mandatory-product-tile.is-selected', section) ||
        qs('.mandatory-product-tile', section);

      if (!selectedTile) return;

      const sel = qs('.mandatory-variant-select', selectedTile);
      const selectedBaseVariantId = safeId(sel?.value) || safeId(selectedTile.dataset.variantId);
      const inclusionVariantId = safeId(selectedTile.dataset.discountVariantId);

      const idToAdd = inclusionVariantId || selectedBaseVariantId;

      if (!idToAdd) return;

      const parentProductId = safeId(selectedTile.dataset.parentProductId);
      const inclusionProductId = safeId(selectedTile.dataset.productId);
      const discountVariantId = inclusionVariantId;
      const baseVariantId = selectedBaseVariantId;

      let originalPriceCents = 0;
      if (sel) {
        const opt = sel.options[sel.selectedIndex];
        originalPriceCents = toCents(opt?.dataset?.priceCents || selectedTile.dataset.priceCents);
      } else {
        originalPriceCents = toCents(selectedTile.dataset.priceCents);
      }

      const discountedPriceCentsRaw = toCents(selectedTile.dataset.discountPriceCents);
      const discountedPriceCents = discountedPriceCentsRaw > 0 ? discountedPriceCentsRaw : originalPriceCents;

      items.push({
        id: Number(idToAdd),
        quantity: qty,
        properties: buildInclusionProperties({
          groupId,
          inclusionType: 'mandatory',
          mainProductId: parentProductId,
          mainVariantId: groupId,
          inclusionProductId,
          baseVariantId,
          discountVariantId,
          originalPriceCents,
          discountedPriceCents,
          section: section.dataset.section || ''
        })
      });
    });

    return items;
  }

  
  // -------------------------
  // PDP Quantity Change → revalidate OPTIONAL CARDS
  // -------------------------
  document.addEventListener('change', (e) => {
    if (e.target?.name === 'quantity') {
      dlog('PDP QTY changed → re-evaluating optional cards');
      initOptionalCardDisabling();
    }
  });

  function initHousingCardsSync() {
    const grid = document.querySelector('.mfw-housing-grid');
    if (!grid) return;

    // Master select that represents the chosen variant combination
    const variantSelect = document.querySelector('select[name="id"]');
    if (!variantSelect) return;

    // Variant JSON injected via Liquid (recommended)
    const jsonEl = document.querySelector('[data-mfw-variants-json]');
    let variants = [];
    try {
      variants = jsonEl ? JSON.parse(jsonEl.textContent || '[]') : [];
    } catch (e) {
      variants = [];
    }
    if (!Array.isArray(variants) || !variants.length) {
      // If JSON is missing, we can still keep click->change behavior, but diffs may not compute.
      variants = [];
    }

    // Build map: id -> variant, and key(option1/2/3) -> variant
    const byId = new Map();
    const byKey = new Map();
    variants.forEach(v => {
      byId.set(String(v.id), v);
      const k = `${v.option1 || ''}||${v.option2 || ''}||${v.option3 || ''}`;
      byKey.set(k, v);
    });

    // Base Housing value (first card title or data-value)
    function getHousingValueFromCard(card) {
      return (
        card?.dataset?.value ||
        card?.dataset?.mfwValue ||
        card?.querySelector('.mfw-housing-title')?.textContent?.trim() ||
        ''
      ).trim();
    }

    // --- NEW: keep .is-selected in sync with the selected combo variant ---
    function syncSelectedHousingCard() {
      // Find the currently selected <option> in the master variant dropdown
      const selectedOpt = variantSelect.options[variantSelect.selectedIndex];
      if (!selectedOpt) return;

      // Housing Type is option1 for your product (Housing Type + Size)
      const selectedHousing = (selectedOpt.dataset?.option1 || '').trim();
      if (!selectedHousing) return;

      grid.querySelectorAll('.mfw-housing-card').forEach((card) => {
        const hv = getHousingValueFromCard(card);
        const isSelected = hv === selectedHousing;

        card.classList.toggle('is-selected', isSelected);

        // Keep the radio checked state aligned too
        const radio = card.querySelector('.mfw-housing-radio');
        if (radio) radio.checked = isSelected;
      });
    }


    function getBaseHousingValue() {
      // Optional override if you ever want: <div class="mfw-housing-grid" data-base-value="White Bracket">
      const override = (grid.dataset && grid.dataset.baseValue) ? String(grid.dataset.baseValue).trim() : '';
      if (override) return override;

      const firstCard = grid.querySelector('.mfw-housing-card');
      return getHousingValueFromCard(firstCard);
    }

    function formatMoneyFromCents(cents) {
      const n = Number(cents || 0) / 100;
      // Use your locale/currency formatting (AUD assumed)
      try {
        return n.toLocaleString(undefined, { style: 'currency', currency: 'AUD' });
      } catch {
        return `$${n.toFixed(2)}`;
      }
    }

    // ✅ Click handling: make sure one click always triggers a real option change
    grid.addEventListener('click', (e) => {
      const label = e.target.closest('.mfw-housing-label');
      if (!label) return;

      const card = label.closest('.mfw-housing-card');
      if (!card || card.classList.contains('is-disabled')) return;

      const radio = card.querySelector('.mfw-housing-radio');
      if (!radio) return;

      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));

        // NEW: theme updates the master select async; schedule UI + diffs after
        scheduleRecompute();
      }

    });

    function recomputeDiffs() {
      if (!variants.length) return;

      const selectedId = String(variantSelect.value || '');
      const selectedVariant = byId.get(selectedId);
      if (!selectedVariant) return;

      const baseHousing = getBaseHousingValue();
      const sizeVal = selectedVariant.option2 || '';
      const opt3Val = selectedVariant.option3 || '';

      // Base price = base housing + current size (+ option3 if any)
      const baseKey = `${baseHousing}||${sizeVal}||${opt3Val}`;
      const baseVariant = byKey.get(baseKey);

      if (!baseVariant) {
        grid.querySelectorAll('.mfw-housing-card .mfw-housing-diff').forEach(el => {
          if (el) el.textContent = '';
        });
        return;
      }

      const basePrice = Number(baseVariant.price || 0);

      // pass 1: compute diffs + detect if ANY positive diff exists
      const rows = [];
      let hasPositiveDiff = false;

      grid.querySelectorAll('.mfw-housing-card').forEach((card) => {
        const hv = getHousingValueFromCard(card);
        if (!hv) return;

        const k = `${hv}||${sizeVal}||${opt3Val}`;
        const v = byKey.get(k);
        if (!v) return;

        const diffEl = card.querySelector('.mfw-housing-diff');
        if (!diffEl) return;

        const price = Number(v.price || 0);
        const diff = price - basePrice;

        rows.push({ diffEl, diff });
        if (diff > 0) hasPositiveDiff = true;
      });

      // pass 2: render text
      rows.forEach(({ diffEl, diff }) => {
        // ✅ if there are NO positive diffs at all, hide everything (no “Included”)
        if (!hasPositiveDiff) {
          diffEl.textContent = '';
          return;
        }

        // Otherwise keep your normal behavior
        if (diff > 0) {
          diffEl.textContent = `+ ${formatMoneyFromCents(diff)}`;
        } else {
          diffEl.textContent = '';
        }

      });

      // A Size-button equivalent of the block above was intended here, but
      // recomputeSizeDiffs was never written — no definition exists anywhere in
      // this file or its history (the call arrived with "Image Variants 5"), so
      // it threw a ReferenceError on every single variant change. The housing
      // diffs are unaffected: the throw landed after they had already rendered,
      // which is why this only ever showed up as console noise.
      // Re-add a call here if size diffs are ever implemented.
    }


    // Important: Theme/app updates the master select asynchronously.
    // So we schedule our recompute AFTER it settles.
    let rafQueued = false;
    function scheduleRecompute() {
      if (rafQueued) return;
      rafQueued = true;

      requestAnimationFrame(() => {
        rafQueued = false;

        // 1) First sync the UI selection
        syncSelectedHousingCard();

        // 2) Then recompute the diffs (uses the correct currently selected variant)
        recomputeDiffs();
      });
    }



    // Initial compute (run twice: immediate + after load tick)
    scheduleRecompute();
    setTimeout(scheduleRecompute, 0);


    // Recompute whenever the master variant changes
    variantSelect.addEventListener('change', scheduleRecompute);

    // Also listen to any option radio changes (Housing/Size)
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t) return;
      if (t.matches('.product-form__single-selector')) {
        scheduleRecompute();
      }
    }, true);
  }

  function initInclusionVariantPriceSync() {
    document.addEventListener('change', (e) => {
      const sel = e.target;
      if (!sel.matches('[data-optional-variant-select], .mandatory-variant-select')) return;

      const card =
        sel.closest('[data-optional-addon]') ||
        sel.closest('.mandatory-product-tile');

      if (!card) return;

      const opt = sel.options[sel.selectedIndex];
      const basePriceCents = parseInt(opt?.dataset?.priceCents || card.dataset.priceCents || '0', 10) || 0;
      const inclusionPriceCents = parseInt(card.dataset.discountPriceCents || '0', 10) || 0;
      // A free ($0.00) inclusion is still active — key off the discount variant.
      const hasInclusion = !!(card.dataset.discountVariantId || '').trim();

      const shownPriceCents = hasInclusion ? inclusionPriceCents : basePriceCents;
      const shownCompareCents = hasInclusion ? basePriceCents : 0;

      const formatMoney = (cents) => {
        const n = Number(cents || 0) / 100;
        try {
          return `$${n.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })}`;
        } catch {
          return `$${n.toFixed(2)}`;
        }
      };

      const priceWrap =
        card.querySelector('[data-optional-price]') ||
        card.querySelector('[data-mandatory-price]');

      if (!priceWrap) return;

      const mainPrice =
        priceWrap.querySelector('.price--discounted, .price--regular');

      if (mainPrice) {
        mainPrice.className = hasInclusion ? 'price--discounted' : 'price--regular';
        mainPrice.innerHTML = `<span class="price-prefix">+</span> ${formatMoney(shownPriceCents)}`;
      }

      let compareEl = priceWrap.querySelector('.price--compare');

      if (shownCompareCents > shownPriceCents) {
        if (!compareEl) {
          const compareWrap = document.createElement(priceWrap.closest('.mandatory-price') ? 'span' : 'span');
          compareWrap.className = priceWrap.closest('.mandatory-price') ? 'price--compare-line' : '';
          compareEl = document.createElement('span');
          compareEl.className = 'price--compare';
          compareWrap.appendChild(compareEl);
          priceWrap.appendChild(compareWrap);
        }
        compareEl.textContent = formatMoney(shownCompareCents);
      } else if (compareEl) {
        const wrap = compareEl.parentElement;
        if (wrap && wrap !== priceWrap && wrap.classList.contains('price--compare-line')) {
          wrap.remove();
        } else {
          compareEl.remove();
        }
      }

      card.dataset.priceCents = String(basePriceCents);
    });
  }

  function toggleDynamicCheckoutForInclusions() {
    const hasInclusions = hasInclusionsOnPage();

    const dynamicButtons = qsa(
      '.shopify-payment-button, .shopify-payment-button__button, [data-shopify="payment-button"]'
    );

    dynamicButtons.forEach((el) => {
      if (hasInclusions) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
      }
    });

    const paymentContainers = qsa('.product-form__payment_container');
    paymentContainers.forEach((el) => {
      if (hasInclusions) {
        // Reliable hide: a class + stylesheet !important rule
        // (.mfw-hide-dynamic-checkout in custom.css) keeps the dynamic-checkout
        // button hidden even after the theme's variant-change handler re-applies
        // an inline display:block on tile/variant clicks (assets/theme.js ~3521).
        // The inline display:none above alone loses to that; the class doesn't.
        // Without this the button reappears mid-session and would let Shop Pay
        // express checkout bypass the inclusion add-to-cart flow.
        el.classList.add('mfw-hide-dynamic-checkout');
        el.style.paddingBottom = '2%';
      } else {
        el.classList.remove('mfw-hide-dynamic-checkout');
        el.style.paddingBottom = '';
      }
    });

    const addButtons = qsa('.product-form__add-button.button.button--primary');
    addButtons.forEach((btn) => {
      if (hasInclusions) {
        btn.style.setProperty('width', '100%', 'important');
        btn.style.setProperty('max-width', '100%', 'important');
        btn.style.setProperty('margin-left', 'auto', 'important');
        btn.style.setProperty('margin-right', 'auto', 'important');
        btn.style.setProperty('display', 'block', 'important');
      } else {
        btn.style.removeProperty('width');
        btn.style.removeProperty('max-width');
        btn.style.removeProperty('margin-left');
        btn.style.removeProperty('margin-right');
        btn.style.removeProperty('display');
      }
    });
  }


  /* =========================
     INIT
  ========================= */
  function init() {

    initOptionalGroups();
    initOptionalCardDisabling();
    initRequiredOptionalGroups();
    initVariantScope();

    // call this alongside your existing setup
    setupOptionalVerticalButtons();
    // after the vertical buttons boot, so their ADD/REMOVE labels can be synced
    initOptionalPreselect();

    initMandatoryGroups();
    initTimeBasedOptions();
    initTooltips();
    initMandatoryTileSelection();
    // after tile selection, so the trigger label reads the default pick
    initMandatoryDropdowns();
    initInclusionVariantPriceSync();
    initHousingCardsSync();
    initAddToCart();
    initCartBatchSync();


    setupTotalRow();

    toggleDynamicCheckoutForInclusions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  // Trigger label for a dropdown group: the single pick's title, "N selected"
  // for a multi-select group, or the placeholder when nothing is chosen.
  function syncOptionalDropdownTrigger(group) {
    if (!group) return;

    const titles = [];
    group.querySelectorAll('[data-optional-addon]').forEach(function (a) {
      const cb = a.querySelector('[data-optional-checkbox][data-dropdown-item]');
      if (!cb || !cb.checked) return;
      const t = a.querySelector('.optional-dropdown-option__title');
      if (t) titles.push(t.textContent.trim());
    });

    const trigger = group.querySelector('[data-optional-dropdown-trigger]');
    const text = trigger && trigger.querySelector('.optional-dropdown-trigger__text');
    if (!text) return;

    text.textContent = !titles.length
      ? 'Select an option'
      : (titles.length === 1 ? titles[0] : titles.length + ' selected');
  }

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-optional-dropdown-trigger]');
    if (trigger) {
      const shell = trigger.closest('.optional-dropdown-group-shell');
      const panel = shell && shell.querySelector('[data-optional-dropdown-panel]');
      if (!panel) return;

      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.hidden = isOpen;

      const icon = trigger.querySelector('.optional-dropdown-trigger__icon');
      if (icon) icon.textContent = isOpen ? '+' : '−';
      return;
    }

    const clearBtn = e.target.closest('[data-dropdown-clear]');
    if (clearBtn) {
      const group = clearBtn.closest('[data-optional-group]');
      if (!group) return;

      const allAddons = group.querySelectorAll('[data-optional-addon]');
      allAddons.forEach(function (a) {
        const cb = a.querySelector('[data-optional-checkbox][data-dropdown-item]');
        if (cb) cb.checked = false;

        const row = a.querySelector('.optional-dropdown-item');
        if (row) row.classList.remove('is-selected');
      });

      group.querySelectorAll('.optional-dropdown-item--none').forEach(function (row) {
        row.classList.add('is-selected');
      });

      const trigger = group.querySelector('[data-optional-dropdown-trigger]');
      const triggerText = trigger && trigger.querySelector('.optional-dropdown-trigger__text');
      if (triggerText) {
        triggerText.textContent = 'Select an option';
      }

      const panel = group.querySelector('[data-optional-dropdown-panel]');
      if (panel && trigger) {
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        const icon = trigger.querySelector('.optional-dropdown-trigger__icon');
        if (icon) icon.textContent = '+';
      }

      group.querySelectorAll('[data-optional-checkbox][data-dropdown-item]').forEach(function (cb) {
        cb.dispatchEvent(new Event('change', { bubbles: true }));
      });

      return;
    }

    const optionBtn = e.target.closest('[data-dropdown-option]');
    if (optionBtn) {
      const item = optionBtn.closest('.optional-dropdown-item');
      const addon = optionBtn.closest('[data-optional-addon]');
      const group = optionBtn.closest('[data-optional-group]');
      const checkbox = addon && addon.querySelector('[data-optional-checkbox]');
      if (!group || !checkbox || checkbox.disabled) return;

      // "Dropdown multi select" keeps every pick; the legacy "Dropdown
      // checkbox" and the explicit "Dropdown single select" swap the pick.
      const isMulti = group.dataset.dropdownMode === 'multi';

      if (!isMulti) {
        const allAddons = group.querySelectorAll('[data-optional-addon]');
        allAddons.forEach(function (a) {
          const cb = a.querySelector('[data-optional-checkbox][data-dropdown-item]');
          if (cb) {
            cb.checked = false;
            const row = a.querySelector('.optional-dropdown-item');
            if (row) row.classList.remove('is-selected');
          }
        });
      }

      group.querySelectorAll('.optional-dropdown-item--none').forEach(function (row) {
        row.classList.remove('is-selected');
      });

      checkbox.checked = isMulti ? !checkbox.checked : true;
      if (item) item.classList.toggle('is-selected', checkbox.checked);

      const dropdownTrigger = group.querySelector('[data-optional-dropdown-trigger]');
      syncOptionalDropdownTrigger(group);

      checkbox.dispatchEvent(new Event('change', { bubbles: true }));

      // Multi-select stays open — closing after every tick would make picking
      // a second item a two-click job.
      const panel = group.querySelector('[data-optional-dropdown-panel]');
      if (!isMulti && panel && dropdownTrigger) {
        panel.hidden = true;
        dropdownTrigger.setAttribute('aria-expanded', 'false');
        const icon = dropdownTrigger.querySelector('.optional-dropdown-trigger__icon');
        if (icon) icon.textContent = '+';
      }

      return;
    }

    // click outside open dropdowns closes them
    if (!e.target.closest('.optional-dropdown-group-shell')) {
      document.querySelectorAll('[data-optional-dropdown-trigger]').forEach(function (btn) {
        btn.setAttribute('aria-expanded', 'false');
        const icon = btn.querySelector('.optional-dropdown-trigger__icon');
        if (icon) icon.textContent = '+';
      });

      document.querySelectorAll('[data-optional-dropdown-panel]').forEach(function (panel) {
        panel.hidden = true;
      });
    }
  });


  // =====================================================================
  // 2026-06-19 — Back-order optional-inclusions shim
  // ---------------------------------------------------------------------
  // Problem:
  //   On products where Early Bird's "Add back-order to cart" intercept
  //   is active, Early Bird's click handler runs (likely document-level
  //   capture) before our regular PDP ATC handler. It performs its own
  //   /cart/add.js for the main product, then redirects to /cart. Our
  //   handler never runs → selected optional inclusions never get added.
  //
  // Fix:
  //   1. Continuously snapshot selected optionals to sessionStorage as
  //      a "draft" whenever the user toggles a checkbox.
  //   2. On the FIRST pointerdown on the ATC button (capture phase),
  //      promote the draft to a "pending" key with a timestamp. We use
  //      pointerdown — not click — because Early Bird intercepts click,
  //      and pointerdown fires earlier in the input pipeline.
  //   3. On every page load (including /cart after Early Bird redirects
  //      there), flushPendingOptionals() reads the pending key, POSTs
  //      /cart/add.js with just the optional items, and reloads /cart so
  //      they appear.
  //   4. Our native ATC handler (top of this file) clears both keys at
  //      start-of-run so the in-stock path doesn't double-add.
  //
  // Why not preempt Early Bird in capture: we'd have to replicate the
  // back-order metadata they write on the line item (custom properties,
  // inventory_policy override, back-order tag for checkout). We don't
  // know what they write. Far safer to let them run and just attach our
  // own line items after.
  // =====================================================================
  (function backOrderOptionalsShim() {
    const DRAFT_KEY   = 'mfw_optional_draft';
    const PENDING_KEY = 'mfw_pending_optionals';
    const TTL_MS      = 5 * 60 * 1000;   // 5 minutes — expire stale snapshots
    const log = (...a) => { try { if (window.MFW_DEBUG) console.log('[mfw-bo-shim]', ...a); } catch (_) {} };

    function toCentsLocal(v) {
      const n = Number(v);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    }
    function safeIdLocal(v) {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    }

    // 2026-07-02: The keepalive add below must ONLY run for Early-Bird
    // back-order products. On those, Early Bird intercepts the ATC click and
    // the theme's normal add-path never runs, so we POST the optionals
    // ourselves. On EVERY OTHER product the theme's own add-path (native MFW
    // handler / UFE bridge / theme) already adds the selected optionals — so
    // firing the keepalive there double-adds them (a duplicate /cart/add.js
    // that races the normal add and lands as a second line item because it
    // carries different line-item properties). Bundles made this especially
    // visible: they add slower server-side (widening the race window) and
    // render as multiple component lines.
    //
    // Signal: snippets/product-buy-buttons.liquid (v3) sets `.is-back-order`
    // on the section — at render time when the variant is unavailable, or at
    // runtime via MutationObserver once Early Bird relabels the button. The
    // button-text regex is a belt-and-braces fallback.
    function isBackOrderPage() {
      if (document.querySelector('.is-back-order')) return true;
      const btn = document.querySelector('[data-action="add-to-cart"], .product-form__add-button');
      if (btn && /back[\s\-]?order/i.test(btn.textContent || '')) return true;
      return false;
    }

    function getQtyLocal() {
      const el = document.querySelector('input[name="quantity"]');
      const n = Number(el && el.value);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }
    function getMainVariantIdLocal() {
      const form = document.querySelector('form[action*="/cart/add"]');
      const sel = form && form.querySelector('select[name="id"]');
      if (sel) {
        const id = safeIdLocal(sel.options[sel.selectedIndex]?.value);
        if (id) return id;
      }
      const inp = form && form.querySelector('input[name="id"]');
      return safeIdLocal(inp && inp.value);
    }

    function collectOptionalsForSnapshot() {
      const items = [];
      const qty = getQtyLocal();
      const mainId = getMainVariantIdLocal();
      document.querySelectorAll('[data-optional-group]').forEach((group) => {
        group.querySelectorAll('[data-optional-addon]').forEach((block) => {
          const cb = block.querySelector('[data-optional-checkbox]');
          if (!cb || !cb.checked) return;
          const addonId = Number(cb.dataset.discountVariantId || cb.dataset.variantId);
          if (!addonId) return;
          const baseVariantId       = safeIdLocal(cb.dataset.baseVariantId || cb.dataset.variantId || block.dataset.variantId);
          const discountVariantId   = safeIdLocal(cb.dataset.discountVariantId || block.dataset.discountVariantId);
          const inclusionProductId  = safeIdLocal(cb.dataset.productId || block.dataset.productId);
          const parentProductId     = safeIdLocal(cb.dataset.parentProductId || block.dataset.parentProductId);
          const originalPriceCents  = toCentsLocal(cb.dataset.priceCents || block.dataset.priceCents);
          const discountedPriceCents = toCentsLocal(cb.dataset.discountPriceCents || block.dataset.discountPriceCents) || originalPriceCents;
          items.push({
            id: addonId,
            quantity: qty,
            properties: {
              _mfw_inclusion: 'optional',
              _mfw_main_variant_id: String(mainId || ''),
              _mfw_main_product_id: String(parentProductId || ''),
              _mfw_inclusion_product_id: String(inclusionProductId || ''),
              _mfw_base_variant_id: String(baseVariantId || ''),
              _mfw_discount_variant_id: String(discountVariantId || ''),
              _mfw_original_price_cents: String(originalPriceCents),
              _mfw_discounted_price_cents: String(discountedPriceCents)
            }
          });
        });
      });
      return items;
    }

    function syncDraft() {
      try {
        const items = collectOptionalsForSnapshot();
        if (items.length) sessionStorage.setItem(DRAFT_KEY, JSON.stringify(items));
        else sessionStorage.removeItem(DRAFT_KEY);
      } catch (_) {}
    }

    // 1) Track optional selections continuously
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-optional-checkbox]')) {
        syncDraft();
      }
    }, false);

    // Also resync on optional dropdown variant changes (price/variant
    // dropdowns inside an addon card change `data-variant-id` indirectly
    // through state held elsewhere; safest to re-collect on any change)
    document.addEventListener('change', function (e) {
      if (e.target && e.target.matches && e.target.matches('[data-optional-variant-select]')) {
        syncDraft();
      }
    }, false);

    // 2) On pointerdown of the ATC button (capture phase), promote draft
    //    to pending AND fire a keepalive POST of the optional items.
    //    pointerdown fires before click, so we capture intent before Early
    //    Bird's click intercept (or any other) redirects.
    //
    //    The keepalive POST is what eliminates the second /cart paint: the
    //    browser keeps this request in-flight even after Early Bird's
    //    navigation, so by the time the customer lands on /cart the
    //    optional inclusions are usually already added. The sessionStorage
    //    snapshot remains as the safety net — flushPendingOptionals() on
    //    /cart cross-checks /cart.js and only POSTs/reloads if the
    //    keepalive add didn't actually land yet.
    document.addEventListener('pointerdown', function (e) {
      const btn = e.target && e.target.closest &&
        e.target.closest('[data-action="add-to-cart"], .product-form__add-button');
      if (!btn) return;

      // Only fire for Early-Bird back-order products. On normal products the
      // theme's own add-path adds the optionals; firing here too double-adds.
      if (!isBackOrderPage()) {
        log('not a back-order page — skipping keepalive (theme handles optionals)');
        return;
      }

      try {
        syncDraft();   // capture any last-second selection
        const draft = sessionStorage.getItem(DRAFT_KEY);
        if (!draft) return;
        const items = JSON.parse(draft);
        if (!Array.isArray(items) || !items.length) return;
        sessionStorage.setItem(PENDING_KEY, JSON.stringify({ items, ts: Date.now() }));
        log('snapshot promoted to pending', items);

        // Fire-and-forget keepalive POST. `keepalive: true` lets the
        // request survive the page unload that Early Bird's redirect to
        // /cart triggers a few hundred ms later.
        try {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: items }),
            keepalive: true
          }).then(function (r) { log('keepalive add status', r.status); })
            .catch(function (err) { log('keepalive add error', err); });
        } catch (err) {
          log('keepalive fetch threw', err);
        }
      } catch (_) {}
    }, true);

    // Overlay shown over the cart while we add the optional inclusions, to
    // avoid the user seeing the cart in two states (incomplete → reload →
    // complete). Renders flush over the viewport with a spinner.
    function showFinalisingOverlay() {
      if (document.getElementById('mfw-bo-shim-overlay')) return;
      const overlay = document.createElement('div');
      overlay.id = 'mfw-bo-shim-overlay';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.innerHTML =
        '<div class="mfw-bo-shim-card">' +
          '<div class="mfw-bo-shim-spinner" aria-hidden="true"></div>' +
          '<div class="mfw-bo-shim-text">Finalising your cart…</div>' +
        '</div>';

      const style = document.createElement('style');
      style.textContent =
        '#mfw-bo-shim-overlay{position:fixed;inset:0;z-index:2147483647;' +
        'background:rgba(255,255,255,0.96);display:flex;align-items:center;' +
        'justify-content:center;font-family:-apple-system,Segoe UI,Arial,sans-serif;}' +
        '#mfw-bo-shim-overlay .mfw-bo-shim-card{text-align:center;color:#1e2d7d;}' +
        '#mfw-bo-shim-overlay .mfw-bo-shim-spinner{width:48px;height:48px;' +
        'border:4px solid #d6d9ea;border-top-color:#1e2d7d;border-radius:50%;' +
        'margin:0 auto 16px;animation:mfwBoShimSpin .8s linear infinite;}' +
        '#mfw-bo-shim-overlay .mfw-bo-shim-text{font-size:15px;font-weight:600;}' +
        '@keyframes mfwBoShimSpin{to{transform:rotate(360deg);}}';

      // Insert as early as possible so the underlying /cart UI is masked
      // before paint. If <body> isn't ready yet (HTML still streaming),
      // attach to <html>.
      (document.head || document.documentElement).appendChild(style);
      (document.body || document.documentElement).appendChild(overlay);
    }

    // 3) On any page load, flush a fresh pending snapshot
    async function flushPendingOptionals() {
      let payload;
      try {
        const raw = sessionStorage.getItem(PENDING_KEY);
        if (!raw) return;
        payload = JSON.parse(raw);
      } catch (_) {
        try { sessionStorage.removeItem(PENDING_KEY); } catch (_) {}
        return;
      }

      if (!payload || !Array.isArray(payload.items) || !payload.items.length) {
        try { sessionStorage.removeItem(PENDING_KEY); } catch (_) {}
        return;
      }
      if (!payload.ts || Date.now() - payload.ts > TTL_MS) {
        log('snapshot expired, dropping');
        try { sessionStorage.removeItem(PENDING_KEY); } catch (_) {}
        return;
      }

      // On /cart, mask the page IMMEDIATELY so the customer never sees the
      // pre-shim cart state. The overlay stays until our reload() lands and
      // the new page replaces it.
      const path = location.pathname || '';
      const onCart = path === '/cart' || path.indexOf('/cart/') === 0;
      if (onCart) showFinalisingOverlay();

      // Consume the snapshot immediately so a slow add doesn't trigger
      // a double-flush on a same-tab refresh during the in-flight request.
      try { sessionStorage.removeItem(PENDING_KEY); } catch (_) {}

      // Keepalive-add path: the pointerdown handler already fired a
      // keepalive POST for these same items, so the cart on the server
      // may already have them. Cross-check /cart.js — if every snapshot
      // item is present, drop the overlay and return WITHOUT a reload.
      // Falls through to the regular POST + reload only on actual miss.
      if (onCart) {
        try {
          const cartResp = await fetch('/cart.js', {
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          });
          if (cartResp.ok) {
            const cart = await cartResp.json();
            // Bundle-aware presence check. A simple optional lands in the cart
            // under the variant id we submitted, so variant/id matching works.
            // A Shopify BUNDLE optional expands into its component line items,
            // so the parent variant id we POSTed is NOT among cart.items[].id —
            // matching on variant id alone false-negatives and we'd double-add.
            // We therefore ALSO match on the `_mfw_inclusion_product_id`
            // line-item property we attach to our keepalive add (Shopify keeps
            // it on the resulting line), plus product_id, so an already-added
            // bundle optional is recognised regardless of how it expands.
            const cartVariantIds = new Set();
            const cartProductIds = new Set();
            const cartInclusionPids = new Set();
            (cart.items || []).forEach(function (it) {
              if (it.variant_id != null) cartVariantIds.add(Number(it.variant_id));
              if (it.id != null)         cartVariantIds.add(Number(it.id));
              if (it.product_id != null) cartProductIds.add(Number(it.product_id));
              if (it.properties && it.properties._mfw_inclusion_product_id) {
                cartInclusionPids.add(String(it.properties._mfw_inclusion_product_id));
              }
            });
            const allPresent = payload.items.every(function (item) {
              if (cartVariantIds.has(Number(item.id))) return true;
              const incPid = item.properties && item.properties._mfw_inclusion_product_id;
              if (incPid && cartInclusionPids.has(String(incPid))) return true;
              if (incPid && cartProductIds.has(Number(incPid))) return true;
              return false;
            });
            if (allPresent) {
              log('keepalive add already landed, no reload needed');
              const ov = document.getElementById('mfw-bo-shim-overlay');
              if (ov) ov.remove();
              return;
            }
            log('keepalive missed some items, falling back to POST+reload');
          }
        } catch (err) {
          log('cart.js precheck failed, falling back to POST+reload', err);
        }
      }

      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items })
        });
        if (!r.ok) {
          log('add.js failed', r.status);
          // Drop the overlay so the (incomplete) cart at least becomes
          // visible — better than a permanently-blocked page.
          const ov = document.getElementById('mfw-bo-shim-overlay');
          if (ov) ov.remove();
          return;
        }
        log('optionals added post-Early-Bird', payload.items);

        if (onCart) {
          location.reload();
        }
      } catch (err) {
        log('flush error', err);
        const ov = document.getElementById('mfw-bo-shim-overlay');
        if (ov) ov.remove();
      }
    }

    // Run the flush as early as possible. If the snapshot is already in
    // sessionStorage and we're being parsed mid-stream on /cart, mask
    // immediately — no need to wait for DOMContentLoaded — so the cart
    // contents never paint in their incomplete state.
    flushPendingOptionals();
  })();

})();
